import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TimerState {
  duration: number;
  timeLeft: number;
  isActive: boolean;
  isPaused: boolean;
  startTime: number | null;
  pausedAt: number | null;
  isOvertime: boolean;
  overtimeSeconds: number;
  setDuration: (duration: number) => void;
  startTimer: () => void;
  pauseTimer: () => void;
  continueTimer: () => void;
  resetTimer: () => void;
  updateTimeLeft: () => void;
}

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => ({
      duration: 25 * 60, // 25 minutes in seconds
      timeLeft: 25 * 60,
      isActive: false,
      isPaused: false,
      startTime: null,
      pausedAt: null,
      isOvertime: false,
      overtimeSeconds: 0,

      setDuration: (duration) =>
        set(() => ({ 
          duration, 
          timeLeft: duration,
          isOvertime: false,
          overtimeSeconds: 0
        })),

      startTimer: () =>
        set(() => ({
          isActive: true,
          isPaused: false,
          startTime: Date.now(),
          pausedAt: null,
          isOvertime: false,
          overtimeSeconds: 0
        })),

      pauseTimer: () =>
        set((state) => ({
          isPaused: true,
          pausedAt: Date.now(),
          timeLeft: state.timeLeft,
        })),

      continueTimer: () =>
        set((state) => {
          const pauseDuration = state.pausedAt ? Date.now() - state.pausedAt : 0;
          return {
            isPaused: false,
            startTime: state.startTime ? state.startTime + pauseDuration : Date.now(),
            pausedAt: null,
          };
        }),

      resetTimer: () =>
        set((state) => ({
          timeLeft: state.duration,
          isActive: false,
          isPaused: false,
          startTime: null,
          pausedAt: null,
          isOvertime: false,
          overtimeSeconds: 0
        })),

      updateTimeLeft: () => {
        const state = get();
        if (!state.isActive || state.isPaused) return;

        if (state.startTime) {
          const elapsedSeconds = Math.floor((Date.now() - state.startTime) / 1000);
          const newTimeLeft = state.duration - elapsedSeconds;

          if (newTimeLeft <= 0 && !state.isOvertime) {
            // First time hitting zero
            set({ isOvertime: true });
          }

          set(() => ({
            timeLeft: Math.max(0, newTimeLeft),
            overtimeSeconds: Math.max(0, -newTimeLeft),
            isActive: true, // Keep timer active even after zero
          }));
        }
      },
    }),
    {
      name: 'timer-storage',
    }
  )
); 